/**
 * Contenido editorial de la cobertura. Las CIFRAS (totales, años, última carga) ya no viven aquí: salen de
 * GET /api/coverage (UI-5, D-UI-7), que las recalcula cada día hábil. Aquí queda lo que la base no sabe:
 * rangos para la frase del chat, notas, cómo se actualiza cada biblioteca y qué no está cubierto.
 */
import type { CollectionKey } from '../utils/institutions';

export interface CoverageEntry {
  institucion: string;
  tipo: string;
  rango: string;
  /** La lee coverageSummary.ts (hueco de 2019 en circulares); hoy ninguna entrada la usa. */
  nota?: string;
}

/** Rangos para la frase «Corpus consultado: …» del chat (utils/coverageSummary.ts). Recuento de documents del 18-10. */
export const COVERAGE: CoverageEntry[] = [
  { institucion: 'SII', tipo: 'Oficios (jurisprudencia administrativa)', rango: '2010–2026' },
  { institucion: 'SII', tipo: 'Resoluciones', rango: '2010–2026' },
  { institucion: 'SII', tipo: 'Circulares', rango: '2010–2026' },
  { institucion: 'SII', tipo: 'Convenios de doble tributación', rango: '1978–2026' },
  { institucion: 'TTA', tipo: 'Jurisprudencia (sentencias de los tribunales)', rango: '2018–2026' },
  { institucion: 'BCN', tipo: 'Leyes tributarias y normas relacionadas (texto completo por artículo)', rango: '1960–2026' },
];

export const NOT_COVERED = [
  'Corte Suprema y Cortes de Apelaciones',
  'Contraloría General de la República',
];

export interface CollectionEditorial {
  /** Barra de años: `years` dibuja cada año con documentos (los huecos se ven); `range` une el primero y el último. */
  bar: 'years' | 'range';
  /** Nota bajo la barra de años. */
  yearsNote?: string;
  /** En vez de `yearsNote`, la nota «X desde AAAA» armada con el primer año de cada tipo en coverage_stats. */
  desdePorTipo?: boolean;
  /** Nota por tipo (clave = tipo en minúscula, como en coverage_stats). */
  tipoNotes?: Record<string, string>;
  /** Cómo se actualiza; solo donde está comprobado. */
  update?: { value: string; note?: string };
  /** Nota general de la biblioteca. */
  note?: { label: string; value: string; note?: string };
  /** Norma más reciente, con su fuente; solo lo comprobado en el texto cargado. */
  latest?: { label: string; value: string; note?: string };
}

export const COVERAGE_EDITORIAL: Record<CollectionKey, CollectionEditorial> = {
  adm: {
    bar: 'years',
    desdePorTipo: true, // los años cambian con cada carga histórica: la nota sale de los datos (hallazgo A, 18-10)
    update: { value: 'cada día hábil, 8:00', note: 'Revisión automática del sitio del SII.' },
  },
  jud: {
    bar: 'years',
    yearsNote: 'Sentencias publicadas por los Tribunales Tributarios y Aduaneros.',
    update: { value: 'por tandas', note: 'Cada tanda se verifica antes de habilitarla en el chat.' },
  },
  ley: {
    bar: 'range',
    yearsNote: 'Año de cada norma, de la más antigua a la más reciente. Texto completo por artículo.',
    note: { label: 'Entre ellas', value: 'LIR, Ley de IVA y Código Tributario', note: 'También el Reglamento de la Ley de IVA.' },
    // Fechas del texto cargado (BCN-LEY-21850-0): publicación del 17-10 y 9.º transitorio, regla general.
    latest: { label: 'Más reciente', value: 'Ley 21.850, para la Reconstrucción Nacional', note: 'Diario Oficial del 17-10-2026; rige desde el 01-11-2026, salvo las normas con vigencia especial.' },
  },
  conv: {
    bar: 'range',
    yearsNote: 'Año de cada convenio, del más antiguo al más reciente.',
  },
};

/** Lo que el corpus no cubre, como chips en Cobertura. El primer año del SII lo agrega Cobertura desde los datos. */
export const NOT_COVERED_CHIPS = [...NOT_COVERED];
